import { ResizeController } from "@lit-labs/observers/resize-controller.js";
import classNames from "classnames";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { HighlightController } from "../../utils/highlight";
import {
  SpotlightController,
  styles as spotlightStyles,
} from "../../utils/spotlight";
import { ZoomController } from "../../utils/zoom";
import { styles } from "../../utils/zoomable-scrollable-slide.styles";

import type { SlideBackgroundColor, SlideState } from "../../types.d";
import type { SlideViewMode } from "../slide/slide";
import type { Ref } from "lit/directives/ref.js";

const CONTENT_PADDING = 40;

const DEFAULT_SLIDE_WIDTH = 960;
const DEFAULT_SLIDE_HEIGHT = 540;
const DESKTOP_MAX_TITLE_HEIGHT = 106;
const DESKTOP_WRAPPED_MAX_TITLE_HEIGHT = 156;
const DESKTOP_MIN_TITLE_HEIGHT = 80;
const MOBILE_MAX_TITLE_HEIGHT = 116;
const MOBILE_WRAPPED_TITLE_HEIGHT = 176;
const MOBILE_MIN_TITLE_HEIGHT = 80;

export const DEFAULT_ZOOM_FILL_SCALE =
  DEFAULT_SLIDE_WIDTH / (DEFAULT_SLIDE_WIDTH - 2 * CONTENT_PADDING);

const LARGE_CONTENT_DEFAULT_SCALE = 1.5;
@customElement("bp-zoomable-slide")
export class ZoomableSlide extends LitElement {
  static styles = [styles, spotlightStyles];

  @property({ attribute: "background-color" })
  backgroundColor?: SlideBackgroundColor = "white";
  contentContainerRef: Ref<HTMLElement> = createRef();

  @property({
    type: Boolean,
    attribute: "hide-shadow",
  })
  hideShadow?: boolean;

  @property({
    type: Boolean,
    attribute: "hide-title",
  })
  hideTitle?: boolean;

  highlightController = new HighlightController();

  @property({
    type: Boolean,
    attribute: "large-content-mode",
  })
  largeContentMode: boolean = false;

  slideHeight = DEFAULT_SLIDE_HEIGHT;

  slideRef: Ref<HTMLElement> = createRef();
  @property()
  slideState?: SlideState;

  slideWidth = DEFAULT_SLIDE_WIDTH;
  spotlightController = new SpotlightController({
    spotlightContainerRef: this.contentContainerRef,
  });

  @property({
    type: Boolean,
    attribute: "title-wraps-2-lines",
  })
  titleWraps2Lines: boolean = false;

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  zoomController = new ZoomController({
    slideRef: this.slideRef,
    minWindowHeight: this.minContentWindowHeight,
    maxWindowHeight: this.maxContentWindowHeight,
    windowWidth: this.slideWidth,
    verticalPadding: CONTENT_PADDING,
    horizontalPadding: CONTENT_PADDING,
    defaultUnitScale: this.defaultUnitScale,
  });

  get defaultUnitScale() {
    return this.largeContentMode ? LARGE_CONTENT_DEFAULT_SCALE : undefined;
  }

  get maxContentWindowHeight() {
    return this.slideHeight - this.minTitleHeight;
  }

  get maxTitleHeight() {
    return this.hideTitle
      ? 0
      : this.viewMode === "default"
        ? this.titleWraps2Lines
          ? DESKTOP_WRAPPED_MAX_TITLE_HEIGHT
          : DESKTOP_MAX_TITLE_HEIGHT
        : this.titleWraps2Lines
          ? MOBILE_WRAPPED_TITLE_HEIGHT
          : MOBILE_MAX_TITLE_HEIGHT;
  }

  get minContentWindowHeight() {
    return this.slideHeight - this.maxTitleHeight;
  }

  get minTitleHeight() {
    return this.hideTitle
      ? 0
      : this.viewMode === "default"
        ? DESKTOP_MIN_TITLE_HEIGHT
        : MOBILE_MIN_TITLE_HEIGHT;
  }
  private contentObserver = new ResizeController(this, {
    callback: (entries) => {
      entries
        .filter((entry) => entry.target.className.includes("scaled-content"))
        .forEach((entry) => {
          this.zoomController.contentHeight = entry.contentRect.height;
          this.zoomController.contentWidth = entry.contentRect.width;
          this.updateState();
        });
    },
  });
  private slideObserver = new ResizeController(this, {
    callback: (entries) => {
      entries
        .filter((entry) => entry.target.className.includes("zoomable-slide"))
        .forEach((entry) => {
          this.slideHeight = entry.contentRect.height;
          this.slideWidth = entry.contentRect.width;
          this.zoomController.minWindowHeight = this.minContentWindowHeight;
          this.zoomController.maxWindowHeight = this.maxContentWindowHeight;
          this.updateState();
        });
    },
  });

  firstUpdated() {
    if (this.contentContainerRef.value)
      this.contentObserver.observe(this.contentContainerRef.value);

    if (this.slideRef.value) this.slideObserver.observe(this.slideRef.value);
  }

  handleContentUpdate(event: Event) {
    let contentEl = (event.target as HTMLSlotElement).assignedNodes({
      flatten: true,
    })[0] as HTMLElement;

    if (contentEl.querySelector("bp-literary-design")) {
      contentEl = contentEl
        .querySelector("bp-literary-design")!
        .shadowRoot?.querySelector("div") as HTMLElement;
    } else if (contentEl.querySelector("bp-ephesians-literary-design")) {
      contentEl = contentEl
        .querySelector("bp-ephesians-literary-design")!
        .shadowRoot?.querySelector("div") as HTMLElement;
    } else if (contentEl.querySelector("bp-macro-literary-design")) {
      contentEl = contentEl
        .querySelector("bp-macro-literary-design")!
        .shadowRoot?.querySelector("div") as HTMLElement;
    }
    this.highlightController.contentEl = contentEl;
    this.zoomController.contentEl = contentEl;
    this.spotlightController.contentEl = contentEl;
    this.updateState();
  }

  render() {
    // This will be overridden by scroll controller.
    const initStyle = "--scroll-transition: 0; --title-transition: 0;";
    const overflowShadowStyles = classNames("overflow-shadow", {
      "overflow-shadow-hidden": this.hideShadow,
    });

    return html`
      <div
        ${ref(this.slideRef)}
        class="zoomable-slide ${this.viewMode} background-${this
          .backgroundColor}"
        style="${initStyle}"
      >
        ${!this.hideTitle ? html`<slot name="title"></slot>` : ""}
        <div class="content-window">
          <div class="transformed-content">
            <div
              class="scaled-content spotlight-container"
              ${ref(this.contentContainerRef)}
            >
              <slot name="content" @slotchange=${this.handleContentUpdate}>
              </slot>
            </div>
          </div>
        </div>
        <div class="${overflowShadowStyles}"></div>
      </div>
    `;
  }

  updateState() {
    this.highlightController.update(this.slideState?.highlightState || {});
    this.zoomController.update(this.slideState?.transformState || {});
    this.spotlightController.update(this.slideState?.spotlightState || {});
  }

  willUpdate() {
    this.updateState();
    this.zoomController.defaultUnitScale = this.defaultUnitScale;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-zoomable-slide": ZoomableSlide;
  }
}
